import {
    possibleTypes,
    type AbstractTypename,
    type AnyPossibleType,
    type PossibleTypename,
    type AnyObjectType,
} from '../derived';

export type ExtractPossibleType<T, K extends AbstractTypename> = Extract<
    T,
    AnyPossibleType<K>
>;

/**
 * Checks if the provided typename is an abstract (`Union` or `Interface`) typename.
 *
 * @param typename
 */
export function isAbstractTypename(
    typename: unknown
): typename is AbstractTypename {
    if (typeof typename !== 'string') return false;
    return typename in possibleTypes;
}

/**
 * Checks whether `typename` is a possible type for `abstractTypename`.
 *
 * @param objectTypename The object typename.
 * @param abstractTypename The abstract typename.
 */
export function isPossibleTypename<K extends AbstractTypename>(
    objectTypename: unknown,
    abstractTypename: K
): objectTypename is PossibleTypename<K> {
    if (typeof objectTypename !== 'string') return false;
    return (possibleTypes[abstractTypename] as string[]).includes(
        objectTypename
    );
}

/**
 * Checks whether the provided value is a possible type of the `union` or `interface` with name `abstractTypename`.
 *
 * @param value
 * @param abstractTypename
 */
export function isPossibleType<
    T extends AnyObjectType,
    K extends AbstractTypename
>(value: T, abstractTypename: K): value is ExtractPossibleType<T, K> {
    if (!value || typeof value !== 'object' || !('__typename' in value))
        return false;
    return isPossibleTypename(value.__typename, abstractTypename);
}

export function couldBePossibleType<
    T extends AnyObjectType,
    K extends AbstractTypename
>(
    value: T | null | undefined,
    abstractTypename: K
): value is ExtractPossibleType<T, K> | null | undefined {
    if (!value) return true;
    if (typeof value !== 'object') return false;
    if (!('__typename' in value) || value.__typename === undefined) return true;
    return isPossibleTypename(value.__typename, abstractTypename);
}

export function asPossibleType<
    T extends AnyObjectType,
    K extends AbstractTypename
>(value: T, abstractTypename: K): ExtractPossibleType<T, K> | undefined {
    if (isPossibleType(value, abstractTypename)) return value;
    return undefined;
}
